import { useState, useEffect, useCallback } from 'react';
import { notificationsService } from '../services/notificationsService';

interface UseNotificationsReturn {
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to manage notification unread count with auto-refresh
 * @param refreshInterval - Interval in ms to auto-refresh count (default: 30000 = 30 seconds)
 */
export function useNotifications(refreshInterval: number = 30000): UseNotificationsReturn {
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const result = await notificationsService.list({ limit: 1 });
      setUnreadCount(result.unreadCount || 0);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch notifications'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    await fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Initial fetch
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Auto-refresh interval
  useEffect(() => {
    if (refreshInterval <= 0) return;

    const intervalId = setInterval(() => {
      fetchUnreadCount();
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [refreshInterval, fetchUnreadCount]);

  return {
    unreadCount,
    isLoading,
    error,
    refetch,
  };
}
